import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi'
import { chatApi, ChatSummary, useSignedIn } from '../../utils/shopChats'
import { userContext } from '../../usercontext'
import theme from '../../theme'

interface Props {
  activeCid: string
  refreshKey: number          // bump to reload the list (a chat was saved / deleted)
  onSelect: (cid: string) => void
  onNew: () => void
  onDeleted: (cid: string) => void
}

const when = (iso: string) => {
  const d = new Date(iso)
  const today = new Date()
  const diff = Math.floor((today.setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000)
  return diff === 0 ? 'Today' : diff === 1 ? 'Yesterday' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const ChatSidebar: React.FC<Props> = ({ activeCid, refreshKey, onSelect, onNew, onDeleted }) => {
  const signedIn = useSignedIn()
  const [chats, setchats] = useState<ChatSummary[]>([])
  const [authmode, setauthmode] = useState<'none' | 'signin' | 'signup'>('none')
  const [authemail, setauthemail] = useState('')
  const [authpassword, setauthpassword] = useState('')
  const [authname, setauthname] = useState('')
  const [autherror, setautherror] = useState('')
  const [authbusy, setauthbusy] = useState(false)

  const submitAuth = async () => {
    if (!authemail.trim() || !authpassword) {
      setautherror('Enter an email and password.')
      return
    }
    setautherror('')
    setauthbusy(true)
    let result: any = authmode === 'signup'
      ? await userContext.registershopper(authemail.trim(), authpassword, authname.trim())
      : await userContext.login(authemail.trim(), authpassword)
    setauthbusy(false)
    if (result && result.status === 200) {
      setauthmode('none'); setauthemail(''); setauthpassword(''); setauthname('')
      // signedIn flips via the userContext subscription - stays right here, chat picks up as-is
    } else {
      setautherror((result && result.msg) || (authmode === 'signin' ? 'Incorrect email or password.' : 'Something went wrong, please try again.'))
    }
  }

  useEffect(() => {
    if (!signedIn) { setchats([]); return }
    chatApi.list().then(setchats).catch(() => setchats([]))
  }, [signedIn, refreshKey])

  const remove = async (c: ChatSummary) => {
    if (!window.confirm(`Delete "${c.title}"? This can't be undone.`)) return
    await chatApi.remove(c.cid)
    setchats((prev) => prev.filter((x) => x.cid !== c.cid))
    onDeleted(c.cid)
  }

  const rename = async (c: ChatSummary) => {
    const title = window.prompt('Rename this chat', c.title)
    if (!title || !title.trim()) return
    await chatApi.rename(c.cid, title.trim())
    setchats((prev) => prev.map((x) => (x.cid === c.cid ? { ...x, title: title.trim() } : x)))
  }

  const removeAll = async () => {
    if (!window.confirm('Delete all your saved chats? This can\'t be undone.')) return
    await chatApi.removeAll()
    setchats([])
    onNew()
  }

  return (
    <Panel>
      <NewButton onClick={onNew}><FiPlus /> New chat</NewButton>
      {signedIn ? (
        <>
          <List>
            {chats.length === 0 && <Empty>Your chats will show up here.</Empty>}
            {chats.map((c) => (
              <Row key={c.cid} $active={c.cid === activeCid} onClick={() => onSelect(c.cid)}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <Title title={c.title}>{c.title}</Title>
                  <Meta>{when(c.updatedat)}</Meta>
                </div>
                <Icons>
                  <IconButton title="Rename" onClick={(e) => { e.stopPropagation(); rename(c) }}><FiEdit2 size={13} /></IconButton>
                  <IconButton title="Delete" onClick={(e) => { e.stopPropagation(); remove(c) }}><FiTrash2 size={13} /></IconButton>
                </Icons>
              </Row>
            ))}
          </List>
          {chats.length > 0 && <TextButton onClick={removeAll}>Delete all my chats</TextButton>}
        </>
      ) : authmode === 'none' ? (
        <GuestBox>
          <b>Chatting as a guest</b>
          <p>Your chat stays while this tab is open and is deleted after 24 hours. Close the tab and it's gone.</p>
          <p>
            <AuthLink onClick={() => setauthmode('signin')}>Sign in</AuthLink> or{' '}
            <AuthLink onClick={() => setauthmode('signup')}>create an account</AuthLink> to keep your chats and come back to them.
          </p>
        </GuestBox>
      ) : (
        <GuestBox>
          <b>{authmode === 'signup' ? 'Create an account' : 'Sign in'}</b>
          {authmode === 'signup' && (
            <AuthInput type="text" placeholder="Name (optional)" value={authname} onChange={(e) => setauthname(e.target.value)} />
          )}
          <AuthInput type="email" placeholder="Email" value={authemail} onChange={(e) => setauthemail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitAuth()} />
          <AuthInput type="password" placeholder="Password" value={authpassword} onChange={(e) => setauthpassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitAuth()} />
          {autherror && <AuthError>{autherror}</AuthError>}
          <AuthRow>
            <AuthSubmit onClick={submitAuth} disabled={authbusy}>{authbusy ? 'Please wait…' : authmode === 'signup' ? 'Create account' : 'Sign in'}</AuthSubmit>
            <AuthLink onClick={() => { setauthmode('none'); setautherror('') }}>Cancel</AuthLink>
          </AuthRow>
          <p>
            {authmode === 'signup' ? 'Already have an account? ' : "Don't have an account? "}
            <AuthLink onClick={() => { setauthmode(authmode === 'signup' ? 'signin' : 'signup'); setautherror('') }}>
              {authmode === 'signup' ? 'Sign in' : 'Create one'}
            </AuthLink>
          </p>
        </GuestBox>
      )}
    </Panel>
  )
}

const Panel = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadow.raised};
  padding: 14px;
  max-height: 560px;
`

const NewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid ${theme.colors.primary};
  color: ${theme.colors.primary};
  background: ${theme.colors.surface};
  border-radius: ${theme.radius.md};
  padding: 9px 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.primarySoft};
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  min-height: 60px;
`

const Row = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: ${theme.radius.md};
  cursor: pointer;
  background: ${(p) => (p.$active ? theme.colors.primarySoft : 'transparent')};
  &:hover {
    background: ${theme.colors.primarySoft};
  }
`

const Title = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Meta = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
`

const Icons = styled.div`
  display: flex;
  gap: 2px;
  opacity: 0.55;
  ${Row}:hover & {
    opacity: 1;
  }
`

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: 3px;
  &:hover {
    color: ${theme.colors.danger};
  }
`

const Empty = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  padding: 6px 4px;
`

const TextButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: ${theme.colors.textMuted};
  text-decoration: underline;
  cursor: pointer;
  align-self: flex-start;
`

const AuthLink = styled.span`
  color: ${theme.colors.primary};
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`

const AuthInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  margin-top: 8px;
  border-radius: ${theme.radius.sm};
  border: 1px solid ${theme.colors.border};
  font-size: 12px;
  outline: none;
  &:focus {
    border-color: ${theme.colors.primary};
  }
`

const AuthRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
`

const AuthSubmit = styled.button`
  border: none;
  border-radius: ${theme.radius.sm};
  background: ${theme.colors.primary};
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  padding: 8px 14px;
  cursor: pointer;
  &:disabled {
    opacity: .6;
    cursor: default;
  }
`

const AuthError = styled.div`
  color: ${theme.colors.danger};
  font-size: 11px;
  margin-top: 6px;
`

const GuestBox = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  line-height: 1.5;
  p {
    margin: 6px 0 0;
  }
  a {
    color: ${theme.colors.primary};
    font-weight: 600;
  }
  b {
    color: ${theme.colors.text};
  }
`

export default ChatSidebar
